/**
 * "We pulled an insurance card — save coverage?" banner.
 *
 * Shows inside the document detail modal when:
 *   metadata.extractions.insurance_card EXISTS
 *   AND metadata.extractions.insurance_card.acted_on_at IS NULL
 *
 * Clicking "Review & save" opens a prefilled coverage-create dialog
 * (`SaveExtractedCoverageDialog`). On submit, the BE atomically stamps
 * `acted_on_at` on the DocRef extraction, so a refetch makes the
 * banner disappear without us writing any explicit dismissal code.
 *
 * Schema note: `metadata.extractions.<kind>` rides inside the
 * `DocumentReferenceMetadata` blob but isn't typed in the OpenAPI spec
 * (per the GDN-1032 follow-up handoff). We define a local TS shape and
 * cast to it; if the BE later types it, swap to the generated type.
 */

import { useState } from 'react';
import {
  CreditCard as CreditCardIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Alert, Button, Stack, Typography } from '@mui/material';

import type { InsuranceCardExtraction } from './extractionTypes';

import { SaveExtractedCoverageDialog } from './SaveExtractedCoverageDialog';

interface InsuranceCardExtractionBannerProps {
  documentReferenceId: string;
  patientId: string | null;
  extraction: InsuranceCardExtraction;
  /** Called after successful coverage save so the parent can refetch
   *  the DocRef detail (causing the banner to disappear once
   *  `acted_on_at` is populated). */
  onSaved?: () => void;
}

export function InsuranceCardExtractionBanner({
  documentReferenceId,
  patientId,
  extraction,
  onSaved,
}: InsuranceCardExtractionBannerProps) {
  const [reviewOpen, setReviewOpen] = useState(false);

  const headline = headlineFromExtraction(extraction);
  // Confidence is reported 0-100 by the BE. Below 50 we add a note that
  // the extraction may be wrong, but we never gate on it — the provider
  // can always edit the form before saving.
  const lowConfidence =
    typeof extraction.confidence === 'number' && extraction.confidence < 50;

  // Without a patient we can't create a coverage. Org-scoped insurance
  // cards have no destination — surface the read-only summary so the
  // user knows we extracted it, but disable the action.
  const canSave = !!patientId;

  return (
    <>
      <Alert
        severity={lowConfidence ? 'warning' : 'info'}
        icon={<CreditCardIcon fontSize="small" />}
        action={
          canSave ? (
            <Button
              size="small"
              endIcon={<ChevronRightIcon />}
              onClick={() => setReviewOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Review & save
            </Button>
          ) : undefined
        }
        sx={{ alignItems: 'center' }}
      >
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Insurance card read — save to {canSave ? 'this patient' : 'a patient'}?
          </Typography>
          {headline && (
            <Typography variant="caption" color="text.secondary">
              {headline}
            </Typography>
          )}
          {lowConfidence && (
            <Typography variant="caption" color="warning.dark">
              Low confidence — please double-check fields before saving.
            </Typography>
          )}
          {!canSave && (
            <Typography variant="caption" color="text.secondary">
              Assign this document to a patient to save the coverage.
            </Typography>
          )}
        </Stack>
      </Alert>

      {canSave && patientId && (
        <SaveExtractedCoverageDialog
          open={reviewOpen}
          patientId={patientId}
          documentReferenceId={documentReferenceId}
          extraction={extraction}
          onClose={() => setReviewOpen(false)}
          onSaved={() => {
            setReviewOpen(false);
            onSaved?.();
          }}
        />
      )}
    </>
  );
}

/**
 * One-liner summary of what we pulled, prioritizing the fields a
 * provider needs to recognize the card. Returns empty string when we
 * have nothing to show (extraction ran but pulled no recognizable
 * fields).
 */
function headlineFromExtraction(e: InsuranceCardExtraction): string {
  const parts: string[] = [];
  if (e.payer_name) parts.push(e.payer_name);
  if (e.member_id) parts.push(`Member ${e.member_id}`);
  if (e.plan_name && !e.payer_name) parts.push(e.plan_name);
  return parts.join(' · ');
}

